import React, { useMemo, useState } from 'react';
import {
  StatusIndicator,
  KeyValuePairs,
  Header,
  SpaceBetween,
  Container,
  FormField,
  DatePicker,
  ColumnLayout,
  Alert,
  Button,
  Badge,
} from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { Task } from 'src/types/helios/Task';
import {
  getTaskStatusType,
  getTaskStatusColor,
  getTaskStatusLabel,
} from 'src/contstant/taskStatusColors';
import { formatDateTime } from 'src/utils/generalUtils';
import SafeHtml from '../SafeHtml';
import { ChangeDueDateModal } from '../modals/ChangeDueDateModal';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import useUserProfile from 'src/hooks/useUserProfile';
import { LanguageDropdown } from '../LanguageDropdown';
import { DEFAULT_LANGUAGE } from 'src/contstant/commonLabels';
import { getTaskContent } from 'src/helper/taskHelper';
import routePaths from 'src/contstant/routePaths';
import FileDisplay, { FileMetadata } from '../FileDisplay';

interface TaskDetailsProps {
  task: Task;
  onDueDateChange?: (dueDate: string) => void;
  isReadOnlyMode?: boolean;
  canChangeDueDate?: boolean;
  isLoading?: boolean;
  onViewAuditQuestionResponse?: () => void;
}

export const TaskDetails: React.FC<TaskDetailsProps> = ({
  task,
  onDueDateChange,
  isReadOnlyMode = false,
  canChangeDueDate = false,
  isLoading,
  onViewAuditQuestionResponse,
}) => {
  const navigate = useNavigate();
  const { businessUnitList } = useBusinessUnit();
  const {
    getUserSelectedLanguage,
    getPreferLanguageList,
    selectUserLanguage,
    getUserProgramId,
  } = useUserProfile();
  const [translationError, setTranslationError] = useState('');
  const getDateValidationRange = () => {
    const today = new Date();
    const todayUTC = new Date(
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
    );

    let baseDate: Date;
    if (task.taskStatus === 'OVERDUE') {
      baseDate = todayUTC;
    } else {
      const existingDueDate = task.dueDate ? new Date(task.dueDate) : todayUTC;
      const existingDueDateUTC = new Date(
        Date.UTC(
          existingDueDate.getFullYear(),
          existingDueDate.getMonth(),
          existingDueDate.getDate(),
        ),
      );
      baseDate = existingDueDateUTC > todayUTC ? existingDueDateUTC : todayUTC;
    }

    const maxDate = new Date(baseDate);
    maxDate.setUTCDate(baseDate.getUTCDate() + 10);

    return { baseDate, maxDate };
  };

  const getBusinessUnitName = (businessUnitId: string | undefined) => {
    if (!businessUnitId) return '-';
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };
  const [dueDate, setDueDate] = useState(() => {
    if (!task.dueDate) return '';
    try {
      return new Date(task.dueDate).toISOString().split('T')[0];
    } catch {
      return '';
    }
  });

  // Update local state when task.dueDate changes from parent
  React.useEffect(() => {
    if (task.dueDate) {
      try {
        setDueDate(new Date(task.dueDate).toISOString().split('T')[0]);
      } catch {
        setDueDate('');
      }
    }
  }, [task.dueDate]);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDate, setPendingDate] = useState('');

  const handleDueDateChange = (value: string) => {
    const selectedDate = new Date(value + 'T00:00:00.000Z');
    const { baseDate, maxDate } = getDateValidationRange();

    if (selectedDate >= baseDate && selectedDate <= maxDate) {
      setPendingDate(value);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmDateChange = () => {
    if (onDueDateChange) {
      const isoDate = new Date(pendingDate + 'T00:00:00.000Z').toISOString();
      onDueDateChange(isoDate);
    }
    setDueDate(pendingDate);
    setShowConfirmModal(false);
    setPendingDate('');
  };

  const handleCancelDateChange = () => {
    setShowConfirmModal(false);
    setPendingDate('');
  };

  const isDateValid = (dateString: string) => {
    if (!dateString) return true;
    const selectedDate = new Date(dateString + 'T00:00:00.000Z');
    const { baseDate, maxDate } = getDateValidationRange();

    return selectedDate >= baseDate && selectedDate <= maxDate;
  };

  const handleAuditClick = () => {
    if (task.auditId) {
      const params = new URLSearchParams();
      params.set('auditId', task.auditId);
      if (task.businessUnit) {
        params.set('businessUnit', task.businessUnit);
      }
      navigate(`${routePaths.AUDIT_SUMMARY.path}?${params}`);
    }
  };

  const taskContent = useMemo(
    () =>
      task
        ? getTaskContent(task, getUserSelectedLanguage(), (error) =>
            setTranslationError(isLoading === false ? error : ''),
          )
        : null,
    [task, getUserSelectedLanguage],
  );

  // Extract media files from task for display
  const taskMediaFiles = useMemo((): FileMetadata[] => {
    if (!task.mediaResponses?.taskDescription) {
      return [];
    }

    return task.mediaResponses.taskDescription.map((mediaData) => ({
      fileName: mediaData.media.fileName || 'document',
      mediaId: mediaData.media.mediaId,
      presignedUrl: mediaData.presignedUrl,
    }));
  }, [task.mediaResponses?.taskDescription]);

  return (
    <>
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h3"
              actions={
                task.questionId && onViewAuditQuestionResponse ? (
                  <Button variant="primary" onClick={onViewAuditQuestionResponse}>
                    Audit Response
                  </Button>
                ) : undefined
              }
            >
              Audit Information
            </Header>
          }
        >
          <ColumnLayout columns={3}>
            <FormField label="Assigned To">{task.assigneeLogin || '-'}</FormField>
            <FormField label="Business Unit">
              {getBusinessUnitName(task.businessUnit)}
            </FormField>
            <FormField label="Division">{task.divisionName || '-'}</FormField>
            <FormField label="Audit Name">
              {task.auditId ? (
                <div className="title-container title-container-no-gap">
                  <Button
                    ariaLabel="View audit summary"
                    iconAlign="right"
                    iconName="external"
                    variant="icon"
                    onClick={handleAuditClick}
                  />
                  <span className="title-hover" onClick={handleAuditClick}>
                    {task.auditName || `Audit ${task.auditId}`}
                  </span>
                </div>
              ) : (
                task.auditName || '-'
              )}
            </FormField>
            <FormField label="Template Name">{task.templateName || '-'}</FormField>
            <FormField label="Location">{task.locationId || '-'}</FormField>
          </ColumnLayout>
        </Container>

        <Container
          header={
            <Header
              variant="h3"
              actions={
                <LanguageDropdown
                  data-testid="task-language-dropdown"
                  selectedLanguage={getUserSelectedLanguage()}
                  defaultLanguage={DEFAULT_LANGUAGE}
                  filterLanguges={getPreferLanguageList()}
                  onLanguageChange={(value) => {
                    selectUserLanguage(value);
                  }}
                />
              }
            >
              Task Information
            </Header>
          }
        >
          <SpaceBetween size="m">
            {translationError && (
              <Alert data-testid="translation-error-alert" type="error">
                Error : {translationError}
              </Alert>
            )}
            <KeyValuePairs
              columns={3}
              items={[
                { label: 'Task ID', value: task.taskId },
                { label: 'Task Name', value: taskContent?.taskName || '' },
                {
                  label: 'Task Type',
                  value: (() => {
                    switch (task.taskType) {
                      case 'FAILED_QUESTION':
                        return 'Failed Question';
                      case 'OBSERVATION':
                        return 'Observation';
                      case 'ADHOC':
                        return 'Adhoc';
                      default:
                        return task.taskType || '-';
                    }
                  })(),
                },
                { label: 'Created By', value: task.createdBy || task.assignedBy || '-' },
                {
                  label: 'Created At',
                  value: task.createdAt
                    ? formatDateTime(task.createdAt, 'DD MMM YYYY')
                    : '-',
                },
                {
                  label: 'Task Status',
                  value: (
                    <SpaceBetween direction="horizontal" size="xs">
                      <StatusIndicator
                        type={getTaskStatusType(task.taskStatus)}
                        colorOverride={getTaskStatusColor(task.taskStatus)}
                        data-testid="status-field"
                      >
                        {getTaskStatusLabel(task.taskStatus)}
                      </StatusIndicator>
                      {task.reopenCount != null &&
                        task.reopenCount > 0 &&
                        task.taskStatus === 'IN_PROGRESS' && (
                          <Badge color="blue" data-testid="reopened-badge">
                            Reopened
                          </Badge>
                        )}
                    </SpaceBetween>
                  ),
                },
                ...(task.taskStatus === 'COMPLETED' && task.completedLate !== undefined
                  ? [
                      {
                        label: 'On Time Completion',
                        value: (
                          <StatusIndicator
                            type={task.completedLate ? 'warning' : 'success'}
                            data-testid={
                              task.completedLate
                                ? 'completed-late-indicator'
                                : 'completed-on-time-indicator'
                            }
                          >
                            {task.completedLate ? 'Late' : 'On Time'}
                          </StatusIndicator>
                        ),
                      },
                    ]
                  : []),
                {
                  label: 'Due Date',
                  value:
                    !canChangeDueDate ||
                    task.taskStatus === 'COMPLETED' ||
                    task.taskStatus === 'CANCELLED' ||
                    task.taskStatus === 'OVERDUE' ? (
                      task.dueDate ? (
                        formatDateTime(task.dueDate, 'DD MMM YYYY')
                      ) : (
                        '-'
                      )
                    ) : (
                      <DatePicker
                        value={dueDate}
                        onChange={({ detail }) => handleDueDateChange(detail.value)}
                        openCalendarAriaLabel={(selectedDate) =>
                          'Choose due date' +
                          (selectedDate ? `, selected date is ${selectedDate}` : '')
                        }
                        placeholder="YYYY/MM/DD"
                        data-testid="due-date-field"
                        isDateEnabled={(date) => {
                          const dateUTC = new Date(
                            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
                          );
                          const { baseDate, maxDate } = getDateValidationRange();

                          return dateUTC >= baseDate && dateUTC <= maxDate;
                        }}
                      />
                    ),
                },
                ...(task.taskStatus === 'COMPLETED' && task.completedAt
                  ? [
                      {
                        label: 'Completed At',
                        value: formatDateTime(task.completedAt),
                      },
                    ]
                  : []),
              ]}
            />
            <FormField label="Task description" stretch>
              <SafeHtml
                html={taskContent?.description || ''}
                mediaResponse={task.mediaResponses?.taskDescription}
              />
            </FormField>
            {taskMediaFiles.length > 0 && (
              <FormField label="Attachments" stretch>
                <FileDisplay files={taskMediaFiles} readonly={true} />
              </FormField>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>

      <ChangeDueDateModal
        visible={showConfirmModal}
        onDismiss={handleCancelDateChange}
        onConfirm={handleConfirmDateChange}
        originalDate={task.dueDate || ''}
        newDate={
          pendingDate ? new Date(pendingDate + 'T00:00:00.000Z').toISOString() : ''
        }
      />
    </>
  );
};
